import { CompleteEvent, Model, SurveyModel } from "survey-core";

import { DateTime } from "@bps/date-time";
import { confirm } from "@bps/fluent-ui";

import { postForm } from "../api/form";
import { renderers } from "../Widgets/replaceWidget";
import { FormDisplaySettings, FormStatusEnum } from "./useFormApi.types";

class FormConfig {
  hideFields: string[] | undefined;
  titleFields?: Record<string, string> | undefined;
}

export const setupModel: (options: {
  modelJson: string;
  expiry?: DateTime;
  setFormStatus?: (data: any) => void;
  formConfig?: FormConfig;
  formId?: string;
  qrcode: boolean;
  preview: boolean;
  formDisplaySettings?: FormDisplaySettings;
  isReadOnly?: boolean;
  documentDownloadCallBackUrl?: string;
  variables?: any;
}) => { form: Model; titleFields?: Record<string, string> } = ({
  modelJson,
  expiry,
  setFormStatus,
  formId,
  qrcode,
  formConfig,
  preview,
  isReadOnly,
  documentDownloadCallBackUrl,
  formDisplaySettings,
  variables
}) => {
  const model = new Model(modelJson);
  model.showCompleteButton = !isReadOnly;
  model.mode = isReadOnly ? "display" : "edit";

  const hideFields = formConfig && formConfig.hideFields;
  const titleFields =
    formConfig && formConfig.titleFields ? formConfig.titleFields : undefined;

  model.getAllQuestions().forEach(x => {
    const renderer = renderers.get(x.getType());
    x.renderAs = renderer ?? x.renderAs;
    if (hideFields && hideFields.find(h => h === x.name)) {
      x.visible = false;
    }
  });

  const onComplete: (
    sender: SurveyModel,
    options: CompleteEvent
  ) => void = async (survey, options) => {
    try {
      options.showDataSavingClear();
      const result =
        !preview && formId && (await postForm(formId, survey.data));
      if (!preview && setFormStatus) {
        if (!result) {
          formId && options.showDataSavingError();
        } else {
          !formId && showData(survey);
          setFormStatus({
            status: FormStatusEnum.Completed,
            formDisplaySettings,
            isReadOnly,
            documentDownloadCallBackUrl,
            variables
          });
        }
      } else {
        showData(survey);
      }
    } catch {
      options.showDataSavingError();
    }
  };

  const status = qrcode ? FormStatusEnum.QrCode : FormStatusEnum.Available;
  model && model.onComplete.add((model, event) => onComplete(model, event));
  model &&
    setFormStatus &&
    setFormStatus({
      status,
      isReadOnly,
      documentDownloadCallBackUrl,
      form: model,
      expiry,
      titleFields,
      preview,
      formDisplaySettings,
      variables
    });
  return { form: model, titleFields };
};

const createResponsePreview = (survey: Model): string => {
  const responses = [];
  for (const key in survey.data) {
    const question = survey
      .getAllQuestions()
      .find(question => question.name === key);
    responses.push({
      sortOrder: question?.visibleIndex,
      text: `${question?.title}: ${survey.data[key]}`
    });
  }
  const response = responses
    .sort((a, b) => (a.sortOrder ?? 1000) - (b.sortOrder ?? 999))
    .map(item => item.text)
    .join("\n\n");
  return response.substring(0, response.length);
};

const showData = (survey: Model) => {
  confirm({
    dialogContentProps: {
      title: "Form data",
      subText: createResponsePreview(survey),
      isMultiline: true,
      styles: { subText: { whiteSpace: "pre-line" } }
    },
    cancelButtonProps: {
      styles: { root: { display: "none" } }
    }
  });
};
